import React, { useState, useEffect, memo } from 'react';
import {
  User,
  Mail,
  ClipboardPenLine,
  HandHelping,
  BookOpen,
  Crown,
  CupSoda,
  QrCodeIcon,
  Settings,
  Clock,
} from 'lucide-react';

// Memoized clock component to prevent unnecessary re-renders
const LiveClock = memo(() => {
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    // Update every second
    const timer = setInterval(() => {
      setTime(new Date());
    }, 1000);

    // Cleanup interval on unmount
    return () => clearInterval(timer);
  }, []);

  return (
    <div className='flex items-center mb-4 space-x-2 text-gray-600'>
      <Clock className='h-5 w-5' />
      <span className='text-lg'>
        {time.toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        })}
      </span>
    </div>
  );
});

LiveClock.displayName = 'LiveClock';

const Home = ({ user }) => {
  const Card = ({ title, children, className }) => (
    <div className={`bg-white rounded-lg shadow p-6 ${className}`}>
      {title && <h2 className='font-semibold mb-4 text-xl'>{title}</h2>}
      {children}
    </div>
  );

  const NavButton = ({
    href,
    icon: Icon,
    label,
    variant = 'default',
    disabled = false,
  }) => {
    const variants = {
      default: 'bg-tree-green hover:bg-tree-green/90',
      danger: 'bg-red-600 hover:bg-red-700',
    };

    return (
      <button
        onClick={() => (window.location.href = href)}
        disabled={disabled}
        className={`${variants[variant]} flex items-center justify-center space-x-2 px-6 py-4 rounded-lg text-white transition-colors w-full`}>
        <Icon className='h-5 w-5' />
        <span className='font-medium'>{label}</span>
      </button>
    );
  };

  const InfoItem = ({ icon: Icon, label, value }) => (
    <div className='flex items-center mb-4 space-x-3'>
      <div className='bg-gray-100 flex h-8 items-center justify-center rounded-full w-8'>
        <Icon className='h-4 text-gray-600 w-4' />
      </div>
      <div>
        <p className='text-gray-500 text-sm'>{label}</p>
        <p className='font-medium'>{value}</p>
      </div>
    </div>
  );

  return (
    <div className='max-w-4xl mx-auto p-6 space-y-6'>
      <LiveClock />
      <h1 className='font-bold mb-6 text-3xl'>
        Welcome to TreeHacks {new Date().getFullYear()}...
      </h1>

      <div className='gap-6 grid grid-cols-1 md:grid-cols-3'>
        {/* User Information Card */}
        <Card className='md:col-span-3'>
          <div className='gap-6 grid grid-cols-1 md:grid-cols-2'>
            <InfoItem
              icon={User}
              label='Organizer Name'
              value={user.attributes.name}
            />
            <InfoItem
              icon={Mail}
              label='Organizer Email'
              value={user.attributes.email}
            />
          </div>
        </Card>

        {/* Main Navigation Card */}
        <Card
          title='Navigation'
          className='md:col-span-2'>
          <div className='gap-4 grid grid-cols-1 lg:grid-cols-3 md:grid-cols-2'>
            <NavButton
              href='/hacker'
              icon={User}
              label='Hacker'
            />
            <NavButton
              href='/mentor'
              icon={HandHelping}
              label='Mentor'
            />
            <NavButton
              href='/judge'
              icon={ClipboardPenLine}
              label='Judge'
            />
            <NavButton
              href='/workshops'
              icon={BookOpen}
              label='Workshops'
            />
            <NavButton
              href='/hardware'
              icon={Settings}
              label='Hardware'
            />
            <NavButton
              href='/boba'
              icon={CupSoda}
              label='Boba'
            />

            {user?.attributes?.isLeadership && (
              <NavButton
                href='/admin'
                icon={Crown}
                label='Admin'
                variant='danger'
              />
            )}
          </div>
        </Card>

        {/* Backend Card */}
        <Card
          title='Backend Tools'
          className='bg-white'>
          <div className='gap-4 grid grid-cols-1'>
            <NavButton
              href='/setup'
              icon={QrCodeIcon}
              label='Scanner Set Up'
              variant='danger'
            />
          </div>
        </Card>
      </div>

      {/* Quick Links or Additional Info could go here */}
    </div>
  );
};

export default Home;
